/**
 * stocktakeResource — the historical stocktake list (#1418), authored against
 * the resource-kit v2 model/view contracts and consumed directly as a
 * `ResourceBundle` by `ResourcePage` (#1805 slice A1).
 *
 * The five cell projections that used to be inline `fields[].render` callbacks
 * now live in `./widgets.tsx` as named widgets — `render` has no legacy
 * carrier, by design, since the parity manifest lands it on `Widget`. Nothing
 * an operator sees moved; `__tests__/stocktakeResource.v2-parity.test.tsx`
 * freezes the cell HTML, the filter schema, `searchable` and the column order,
 * and the repo-wide CSV lock freezes the export text.
 *
 * Row-aware detail dispatch is the load-bearing behaviour: `detail.surface:
 * 'page'` makes a row click navigate (no drawer), and `detail.pagePath(row)`
 * — the row-aware signature (#1418) — sends a LIVE stocktake
 * (`open`/`counted`) to the count page and a TERMINAL one
 * (`committed`/`cancelled`) to the read-only review page. That is exactly the
 * bespoke `handleRowClick` branch, now framework-owned. The "open new
 * stocktake" CTA (DatePicker + mutation) has no framework slot, so it stays
 * in the thin `StocktakeListPage` wrapper (the StockLevels precedent).
 *
 * Pagination: `/api/stocktakes` is SERVER-paginated (SQL LIMIT/OFFSET, `size`
 * ≤ 100, full `total`) — NOT a capped collection — so this stays on the
 * default server path. Client mode would silently hide every stocktake past
 * the fetched cap (#1289). The endpoint also exposes no `search` param, and
 * every field opts out of filter generation, so `capabilities.search: false`
 * keeps the framework from rendering an inert free-text box.
 *
 * Route-gated by `<PermissionGuard>` in `App.tsx` (STOCKTAKE_READ).
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import { stocktakeAPI, toListResult, type Stocktake, type StocktakeStatus } from '@/services';
import { PERMISSIONS } from '@/permissions';

// Live statuses continue counting; terminal statuses open the read-only review.
const LIVE_STATUSES: readonly StocktakeStatus[] = ['open', 'counted'];

/** Row-aware detail path (#1418): open/counted → count, else → review. */
function stocktakeDetailPath(row: Stocktake): string {
  const id = encodeURIComponent(row.id);
  return LIVE_STATUSES.includes(row.status) ? `/stocktake/${id}/count` : `/stocktake/${id}/review`;
}

const f = createFieldConstructors<Stocktake>();

// `filterable: false` on every column is the model's statement, not a view's:
// `/api/stocktakes` accepts no filter param at all, so there is no view in
// which any of these could be filtered. It is also what keeps the derived
// QueryBuilder schema empty, which is what the suppressed search box rests on.
export const stocktakeModel = defineModel<Stocktake>()({
  name: 'stocktake',
  idField: 'id',
  recName: 'week_start',
  fields: {
    week_start: f.text({
      label: 'stocktake.list.col.week',
      filterable: false,
      widget: 'stocktakeWeek',
    }),
    // Closed-set enum from the wire type, but declared `text` here because the
    // badge routes the label through the `stocktake.status.*` keys itself and
    // an `enum` type would additionally demand an option table nothing reads.
    status: f.text({
      label: 'stocktake.list.col.status',
      filterable: false,
      widget: 'stocktakeStatus',
    }),
    opened_at: f.text({
      label: 'stocktake.list.col.openedAt',
      filterable: false,
      widget: 'stocktakeOpenedAt',
    }),
    closed_at: f.text({
      label: 'stocktake.list.col.closedAt',
      filterable: false,
      widget: 'stocktakeClosedAt',
    }),
    opened_by: f.text({
      label: 'stocktake.list.col.openedBy',
      filterable: false,
      widget: 'stocktakeOpenedBy',
    }),
  },
});

export const stocktakeListView = defineListView(stocktakeModel, {
  items: [
    { field: 'week_start' },
    { field: 'status' },
    { field: 'opened_at' },
    { field: 'closed_at' },
    { field: 'opened_by' },
  ],
  // No `search` param on the endpoint + no filterable field ⇒ suppress the box.
  capabilities: { search: false },
});

// The ledger is read-only: no create, no edit, and therefore no form tabs. The
// legacy detail surface is a full page, not the record form (see `host.detail`).
export const stocktakeFormView = defineFormView(stocktakeModel, {
  groupDisplay: 'tab',
  children: [],
});

export const stocktakeResource: ResourceBundle<Stocktake, typeof stocktakeModel.fields> = {
  model: stocktakeModel,
  list: stocktakeListView,
  form: stocktakeFormView,
  host: {
    api: {
      // `stocktakeAPI.list` takes `{ page, size }`; the shared table state uses
      // `limit`. Map limit → size on the way out and back again on the way in
      // (#2167 PR-2) — `size` is the kernel's key on both legs.
      list: async (params) =>
        toListResult(await stocktakeAPI.list({ page: params.page, size: params.limit })),
    },
    label: 'stocktake.list.title',
    slug: 'stocktakes',
    listTitle: 'stocktake.list.title',
    listDescription: 'stocktake.list.subtitle',
    permission: { list: PERMISSIONS.STOCKTAKE_READ, read: PERMISSIONS.STOCKTAKE_READ },
    // `/api/stocktakes` accepts `status` / `page` / `size` and NOTHING else —
    // there is no sort param to send, and `stocktakeAPI.list` forwards only
    // page and size. Written out rather than left absent because on this seam
    // absent used to mean "unrestricted": `createColumns` marked all five
    // headers sortable and each click reordered nothing (#2114). The route is
    // bespoke, so there is no generated constant to bind by reference.
    sortable: [],
    detail: { surface: 'page', pagePath: stocktakeDetailPath },
  },
};
